import * as R from 'ramda';
import objectToFormData from 'object-to-formdata';
import { fetchApplication } from './index';
import * as actionTypes from '../constants/actionTypes';
import * as requestTypes from '../constants/requestTypes';
import setRequestInProcess from '../actions/request';
import api from '../utils/api';
import { successNotification, notInternalRespondErrorNotification, downloadResponseFile } from '../utils';

const isValidProperty = (property) => {
  return property && property.address && property.address.length > 0;
};


const COLUMN_CONFIGS = {
  PICK_COLUMNS: ['id'],
  OMIT_COLUMNS: ['key', 'valid', '_new', 'editable', 'meta', 'edidable_on_add']
};

const prepareProperty = (property, column, value, targetId, targetType) => {
  const columnsToPick = [...COLUMN_CONFIGS.PICK_COLUMNS, column];
  const prepared = property.id
    ? R.pick(columnsToPick, property)
    : R.omit(COLUMN_CONFIGS.OMIT_COLUMNS, property);

  return {
    ...prepared,
    [column]: value,
    target_id: targetId,
    target_type: targetType
  };
};


export const fetchSchedules = (appToken) => (dispatch) => {
  const requestType = requestTypes.SCHEDULES;
  const url = `/applications/${appToken}/re_schedules`;

  dispatch(setRequestInProcess(true, requestType));
  return api
    .get(url)
    .then(({ data }) => {
      dispatch(setRequestInProcess(false, requestType));
      dispatch(fetchApplicationSchedulesSuccess(data.collection));
    })
    .catch((response) => {
      dispatch(setRequestInProcess(false, requestType));
      notInternalRespondErrorNotification(response);
    });
};

export const fetchSchedulesDirectly = (appToken) => (dispatch) => {
  const requestType = requestTypes.SCHEDULES;
  const url = `/applications/${appToken}/re_schedules`;

  dispatch(setRequestInProcess(true, requestType));
  return api
    .get(url)
    .then(({ data }) =>
      data
    )
    .catch((response) => {
      notInternalRespondErrorNotification(response);
    });
};

export const batchUpdateSchedules = (appToken, schedules, column, value, targetId, targetType) => (dispatch) => {
  const preparedProperties = schedules.map((property) => {
    const preparedProperty = { ...property };
    preparedProperty[column] = value;
    preparedProperty.target_id = targetId;
    preparedProperty.target_type = targetType;
    return preparedProperty;
  });

  const requestType = requestTypes.SCHEDULES;
  const url = `/applications/${appToken}/re_schedules/batch_update`;

  dispatch(setRequestInProcess(true, requestType));
  return api
    .post(url, { re_schedules: preparedProperties })
    .then(({ data }) => {
      dispatch(setRequestInProcess(false, requestType));
    })
    .catch((response) => {
      dispatch(setRequestInProcess(false, requestType));
      notInternalRespondErrorNotification(response);
    });
};

export const fetchSchedulesFromExternal = (appToken, borrower = [], with_replace = false) => (dispatch) => {
  const requestType = requestTypes.SCHEDULES;
  const url = `/applications/${appToken}/re_schedules/fetch`;

  dispatch(setRequestInProcess(true, requestType));
  return api
    .post(url, { borrower, with_replace })
    .then((response) => {
      dispatch(fetchApplicationSchedulesFromExternalSuccess(response));
      dispatch(setRequestInProcess(false, requestType));
    })
    .catch((response) => {
      dispatch(setRequestInProcess(false, requestType));
      notInternalRespondErrorNotification(response);
    });
};

export const fetchSchedulesFromExternalDirectly = (appToken, borrower = [], with_replace = false) => (dispatch) => {
  const requestType = requestTypes.SCHEDULES;
  const url = `/applications/${appToken}/re_schedules/fetch`;

  dispatch(setRequestInProcess(true, requestType));
  return api
    .post(url, { borrower, with_replace })
    .then(({ data }) => data)
    .catch((response) => {
      dispatch(setRequestInProcess(false, requestType));
      notInternalRespondErrorNotification(response);
    });
};

export const editApplicationScheduleRequest = (target, property, column, value, targetId, targetType, appToken) => (dispatch) => {
  const preparedProperty = prepareProperty(property, column, value, targetId, targetType);
  const isUpdate = Boolean(property.id);

  const url = isUpdate
    ? `/applications/${appToken}/re_schedules/${property.id}`
    : `/applications/${appToken}/re_schedules`;

  const method = isUpdate ? 'patch' : 'post';
  const payload = { re_schedule: preparedProperty };

  return api[method](url, payload)
    .then(({ data }) => {
      dispatch(editApplicationSchedule({
        property: data.reSchedule,
        key: property.key
      }));
      successNotification('Property was successfully updated');
    })
    .catch((response) => {
      notInternalRespondErrorNotification(response);
    });
};

export const removeApplicationScheduleRequest = (target, appToken, property) => (dispatch) => {
  if (!property.id) {
    dispatch(removeApplicationSchedule(property.key));
    return Promise.resolve();
  }

  const url = `/applications/${appToken}/re_schedules/${property.id}`;

  return api
    .delete(url)
    .then(({ data }) => {
      dispatch(removeApplicationSchedule(property.key));
      dispatch(fetchApplication(appToken));
      successNotification('Property was successfully removed');
    })
    .catch((response) => {
      notInternalRespondErrorNotification(response);
    });
};

export const downloadApplicationTemplate = (appToken) => (dispatch) => {
  const url = `/applications/${appToken}/re_schedules/template`;

  return api
    .get(url)
    .then((response) => {
      downloadResponseFile('application_template.xlsx', response);
      successNotification('Template downloaded successfully');
    })
    .catch((response) => {
      notInternalRespondErrorNotification(response);
    });
};

export const notifyLoanOfficer = (appToken) => (dispatch) => {
  const url = `/applications/${appToken}/re_schedules/complete`;

  return api
    .post(url)
    .then((response) => {
      successNotification('Information has been submitted. Loan Officers have been notified.');
    })
    .catch((response) => {
      notInternalRespondErrorNotification(response);
    });
};

export const importApplicationScheduleRequest = (file, appToken, targetContactId) => (dispatch) => {
  const requestType = requestTypes.SCHEDULES;
  const secondRequestType = requestTypes.SCHEDULES_IMPORT;
  const url = `/applications/${appToken}/re_schedules/import`;
  const formData = objectToFormData({ import: { asset: undefined } });
  formData.append('import[asset]', file);
  formData.append('import[target_contact_id]', targetContactId);

  dispatch(setRequestInProcess(true, requestType));
  dispatch(setRequestInProcess(true, requestType));

  return api
    .post(url, formData)
    .then(({ data }) => {
      dispatch(setRequestInProcess(false, requestType));
      dispatch(updateImportId(data.import.id));
      dispatch(fetchApplication(appToken));
      successNotification('Your file has been placed to the queue. Please reload this page in a couple of minutes.');
    })
    .catch((response) => {
      dispatch(setRequestInProcess(false, requestType));
      notInternalRespondErrorNotification(response);
    });
};

export const uploadApplicationScheduleRequest = (appToken, file, withReplace, targetContactId) => (dispatch) => {
  const requestType = requestTypes.SCHEDULES
  const requestTypeForImport = requestTypes.SCHEDULES_IMPORT;
  const url = `/applications/${appToken}/re_schedules/upload`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('with_replace', withReplace);

  dispatch(setRequestInProcess(true, requestType));
  dispatch(setRequestInProcess(true, requestTypeForImport));

  return api
    .post(url, formData)
    .then(({ data }) => {
      dispatch(setRequestInProcess(false, requestType));
      dispatch(setRequestInProcess(false, requestTypeForImport));
      dispatch(fetchApplication(appToken));
      successNotification('Uploaded successfully');
    })
    .catch((response) => {
      dispatch(setRequestInProcess(false, requestType));
      dispatch(setRequestInProcess(false, requestTypeForImport));
      notInternalRespondErrorNotification(response);
    });
};

export const checkApplicationScheduleImportStatusRequest = (importId, appToken) => (dispatch) => {
  const requestType = requestTypes.SCHEDULES_CHECK_IMPORT_STATUS;
  const url = `/applications/${appToken}/re_schedules/import/${importId}`;

  dispatch(setRequestInProcess(true, requestType));

  return api
    .get(url)
    .then(({ data }) => {
      dispatch(setRequestInProcess(false, requestType));
      dispatch(checkingImportStatus(data.import));
    })
    .catch((response) => {
      dispatch(setRequestInProcess(false, requestType));
      notInternalRespondErrorNotification(response);
    });
};


export const addApplicationScheduleRequest = (target, property) => (dispatch, getState) => {
  const currentProperties = getState().schedules.properties;
  dispatch(addApplicationSchedule([...currentProperties, property]));
};

export const addApplicationSchedule = (data) => ({
  type: actionTypes.APPLICATION_SCHEDULES_ADD,
  payload: { data }
});

export const removeApplicationSchedule = (data) => ({
  type: actionTypes.APPLICATION_SCHEDULES_REMOVE,
  payload: { data }
});

export const editApplicationSchedule = (data) => ({
  type: actionTypes.APPLICATION_SCHEDULES_UPDATE,
  payload: { data }
});

export const fetchApplicationSchedulesSuccess = (payload) => ({
  type: actionTypes.APPLICATION_SCHEDULES_FETCH_SUCCESS,
  payload
});

export const fetchApplicationSchedulesFromExternalSuccess = (payload) => ({
  type: actionTypes.APPLICATION_SCHEDULES_FROM_EXTERNAL_FETCH_SUCCESS,
  payload
});

export const resetSchedules = () => ({
  type: actionTypes.APPLICATION_SCHEDULES_RESET
});

const updateImportId = (data) => ({
  type: actionTypes.APPLICATION_SCHEDULES_UPDATE_IMPORT_ID,
  payload: { data }
});

const checkingImportStatus = (data) => ({
  type: actionTypes.APPLICATION_SCHEDULES_CHECK_IMPORT_STATUS,
  payload: { data }
});
